<template>
    <div class="translation-row" :class="rowClass" :style="{ borderLeftColor: borderColor }">
        <div class="row-grid" @keydown="onKeyDown">
            <div class="pane meta">
                <div class="field-title">{{ componentLabel }}</div>
                <b-badge :variant="statusVariant" pill class="mt-1 align-self-start f-9">{{ statusLabel }}</b-badge>
            </div>

            <div class="pane source">
                <div class="pane-content">
                    <template v-if="isImage">
                        <div class="image-fields">
                            <div class="image-field">
                                <div class="image-field-label" v-tr>Image|Image</div>
                                <input-image :value="sourceSrc" size="md" disabled />
                            </div>
                            <div class="image-field">
                                <div class="image-field-label" v-tr>Alt Text|Texte Alt</div>
                                <div class="plaintext-view">{{ sourceAlt || "-" }}</div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <text-editor v-if="isRichtext" :value="sourceValue" readonly strip-colors class="h-100" />
                        <div v-else class="plaintext-view">{{ sourceValue || "-" }}</div>
                    </template>
                </div>
            </div>

            <div class="pane target">
                <div class="pane-content">
                    <template v-if="isImage">
                        <div class="image-fields">
                            <div class="image-field">
                                <div class="image-field-label" v-tr>Image|Image</div>
                                <input-image v-model="targetSrc" size="md" @input="markDirty" />
                            </div>
                            <div class="image-field">
                                <div class="image-field-label" v-tr>Alt Text|Texte Alt</div>
                                <b-textarea v-model="targetAlt" rows="2" :spellcheck="true" no-resize
                                    @input="markDirty" />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <text-editor v-if="isRichtext" v-model="targetValue" strip-colors actions-on-hover :readonly="translating">
                            <template #actions>
                                <b-btn size="xs" variant="light-primary" @click="copySource" v-b-tooltip
                                    :title="'Copy source|Copier la source' | tr">
                                    <i class="fas fa-arrow-right-from-dotted-line"></i>
                                </b-btn>
                                <b-btn v-if="dirty" size="xs" variant="light-danger" @click="resetField" v-b-tooltip
                                    :title="'Reset|Réinitialiser' | tr">
                                    <i class="fas fa-rotate-left"></i>
                                </b-btn>
                                <b-btn size="xs" variant="light-primary" class="ml-auto" @click="autoTranslate"
                                    :disabled="translating">
                                    <i v-if="translating" class="fas fa-spinner fa-spin mr-1"></i>
                                    <i v-else class="fas fa-language mr-1"></i>
                                    <span v-tr>Auto-translate|Traduction auto</span>
                                </b-btn>
                            </template>
                        </text-editor>
                        <template v-else>
                            <div class="row-textarea position-relative">
                                <b-textarea v-model="targetValue" rows="3" :spellcheck="true" no-resize :readonly="translating" />
                                <div class="row-actions position-absolute" style="bottom: 0">
                                    <b-btn size="xs" variant="light-primary" @click="copySource" v-b-tooltip
                                        :title="'Copy source|Copier la source' | tr">
                                        <i class="fas fa-arrow-right-from-dotted-line"></i>
                                    </b-btn>
                                    <b-btn v-if="dirty" size="xs" variant="light-danger" @click="resetField" v-b-tooltip
                                        :title="'Reset|Réinitialiser' | tr">
                                        <i class="fas fa-rotate-left"></i>
                                    </b-btn>
                                    <b-btn size="xs" variant="light-primary" class="ml-auto" @click="autoTranslate"
                                        :disabled="translating">
                                        <i v-if="translating" class="fas fa-spinner fa-spin mr-1"></i>
                                        <i v-else class="fas fa-language mr-1"></i>
                                        <span v-tr>Auto-translate|Traduction auto</span>
                                    </b-btn>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TextEditor from "@/components/editor/text-editor.vue";
import InputImage from "@/components/editor/properties/inputs/input-image.vue";

export default {
    name: `translation-manager-row`,
    components: { TextEditor, InputImage },
    props: {
        component: { type: Object, required: true },
        componentKey: { type: String, default: null },
        sourceLocale: { type: String, required: true },
        targetLocale: { type: String, required: true },
        translating: { type: Boolean, default: false },
    },
    data() {
        return {
            dirty: false,
            savedValue: null,
        };
    },
    computed: {
        isImage() {
            return this.component.type === `image`;
        },
        isRichtext() {
            return this.component.type === `text`;
        },
        componentLabel() {
            return this.getFieldLabel(this.componentKey, this.component);
        },
        // Text component values
        sourceValue() {
            return this.getFieldValue(`text`, this.sourceLocale);
        },
        targetValue: {
            get() {
                return this.getFieldValue(`text`, this.targetLocale);
            },
            set(value) {
                this.setFieldValue(`text`, this.targetLocale, value);
                this.markDirty();
            },
        },
        // Image component values
        sourceSrc() {
            return this.getFieldValue(`src`, this.sourceLocale);
        },
        targetSrc: {
            get() {
                return this.getFieldValue(`src`, this.targetLocale);
            },
            set(value) {
                this.setFieldValue(`src`, this.targetLocale, value);
            },
        },
        sourceAlt() {
            return this.getFieldValue(`alt`, this.sourceLocale);
        },
        targetAlt: {
            get() {
                return this.getFieldValue(`alt`, this.targetLocale);
            },
            set(value) {
                this.setFieldValue(`alt`, this.targetLocale, value);
            },
        },
        status() {
            const source = this.sourceValue || ``;
            const target = this.targetValue || ``;
            if (!target.trim()) {
                return `not_translated`;
            }
            if (source === target) {
                return `same`;
            }
            return `translated`;
        },
        statusLabel() {
            switch (this.status) {
                case `not_translated`: return this.$tr(`Not translated|Non traduit`);
                case `same`: return this.$tr(`Same|Identique`);
                default: return this.$tr(`Translated|Traduit`);
            }
        },
        statusVariant() {
            switch (this.status) {
                case `not_translated`: return `light-danger`;
                case `same`: return `light-primary`;
                default: return `light-success`;
            }
        },
        rowClass() {
            return {
                "is-not-translated": this.status === `not_translated`,
                "is-translated": this.status === `translated`,
                "is-same": this.status === `same`,
                "is-dirty": this.dirty,
            };
        },
        borderColor() {
            switch (this.status) {
                case `not_translated`: return `#ffa0a4`; // danger/red
                case `same`: return `#6dffed`; // primary/blue
                default: return `rgb(166 227 135)`; // success/green
            }
        },
    },
    mounted() {
        this.saveSavedValues();
    },
    methods: {
        saveSavedValues() {
            if (this.isImage) {
                this.savedValue = {
                    src: this.targetSrc || ``,
                    alt: this.targetAlt || ``
                };
            } else {
                this.savedValue = this.targetValue || ``;
            }
        },
        getFieldLabel(componentKey, component) {
            const keyLabels = {
                title: this.$tr(`Title|Titre`),
                subtitle: this.$tr(`Subtitle|Sous-titre`),
                description: this.$tr(`Description|Description`),
                button: this.$tr(`Button|Bouton`),
                altButton: this.$tr(`Alt Button|Bouton alternatif`),
                text: this.$tr(`Text|Texte`),
                image: this.$tr(`Image|Image`),
            };

            if (keyLabels[componentKey]) {
                return keyLabels[componentKey];
            }

            return componentKey || component.type;
        },
        getFieldValue(field, locale) {
            const value = this.component[field];
            if (value == null) {
                return ``;
            }
            if (typeof value !== `object`) {
                return value;
            }
            return value[locale] || ``;
        },
        setFieldValue(field, locale, value) {
            let fieldValue = this.component[field];
            if (fieldValue == null || typeof fieldValue !== `object`) {
                this.$set(this.component, field, {});
                fieldValue = this.component[field];
            }
            this.$set(fieldValue, locale, value);
        },
        markDirty() {
            if (this.isImage) {
                const currentSrc = this.targetSrc || ``;
                const currentAlt = this.targetAlt || ``;
                this.dirty = currentSrc !== this.savedValue.src || currentAlt !== this.savedValue.alt;
            } else {
                const current = this.targetValue || ``;
                this.dirty = current !== this.savedValue;
            }
            this.$emit(`dirty-changed`, { component: this.component, dirty: this.dirty });
        },
        copySource() {
            if (this.isImage) {
                this.targetSrc = this.sourceSrc || ``;
                this.targetAlt = this.sourceAlt || ``;
                this.markDirty();
            } else {
                this.targetValue = this.sourceValue || ``;
            }
        },
        resetField() {
            if (this.isImage) {
                this.targetSrc = this.savedValue.src || ``;
                this.targetAlt = this.savedValue.alt || ``;
            } else {
                this.targetValue = this.savedValue || ``;
            }
            this.dirty = false;
            this.$emit(`dirty-changed`, { component: this.component, dirty: false });
        },
        autoTranslate() {
            this.$emit(`auto-translate`, this.component);
        },
        onKeyDown(event) {
            if (event.ctrlKey && event.shiftKey && String(event.key).toLowerCase() === `c`) {
                event.preventDefault();
                this.copySource();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.translation-row {
    background: white;
    border-left: 6px solid #dd1a1a;

    &.is-dirty {
        background: #fffde7;
    }
}

.translation-row+.translation-row {
    border-top: 1px solid #eee;
}

.row-grid {
    display: grid;
    grid-template-columns: var(--translation-grid, 220px minmax(0, 1fr) minmax(0, 1fr));
    align-items: stretch;
}

.pane {
    display: flex;
    flex-direction: column;
}

.pane.meta {
    justify-content: start;
    gap: 4px;
    text-align: left;
    padding: 8px 16px;

    .field-title {
        font-weight: 600;
        text-transform: capitalize;
    }

    .field-context {
        font-size: 12px;
    }
}

.pane-content {
    min-height: 84px;
    height: 100%;
}

.pane-content:not(:has(.text-editor-container)) {
    padding: 8px;
    overflow: hidden;
}

.pane.source {
    background: #f9f9f9;
}

.pane.target {
    .pane-content {
        background: white;
    }
}

.row-actions {
    display: flex;
    align-items: center;
    margin-top: auto;
    width: 100%;
    padding: 2px 4px;
}

.row-actions .btn {
    min-width: 36px;
}

.row-textarea {
    .row-actions {
        opacity: 0;
        transition: opacity 0.15s;
    }

    &:hover .row-actions {
        opacity: 1;
    }
}

.plaintext-view {
    white-space: pre-wrap;
    min-height: 72px;
}

.image-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.image-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.image-field-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #666;
}

@media (max-width: 992px) {
    .row-grid {
        grid-template-columns: 1fr;
    }
}
</style>
